import { useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import FlagIcon from '@mui/icons-material/Flag';
import QueueIcon from '@mui/icons-material/Queue';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MapIcon from '@mui/icons-material/Map';
import PersonIcon from '@mui/icons-material/Person';
import ScienceIcon from '@mui/icons-material/Science';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import { usePermissions } from '../../hooks/use-permissions.js';
import { Jobs } from './AdminJobs';
import { Lookup } from './AdminLookup';
import { Operations as FarmQA } from './farmqa/Operations';
import { Downloads } from './AdminDownloads';
import { FeatureFlags } from './AdminFeatureFlags';
import { Queues } from './AdminQueues';
import { Map } from './AdminMap';
import { GrowerExport } from './AdminGrowerExport';
import { Planting as PlantingUploads } from './uploads/Planting';
import { Yield as YieldUploads } from './uploads/Yield';
import { Lookup as UploadsLookup } from './uploads/Lookup';
import { Tokens as DeereTokens } from './deere/Tokens';
import { Subscriptions as DeereSubscriptions } from './deere/Subscriptions';

const allRoutes = [
  {
    path: '/lookup',
    label: 'Lookup',
    icon: SearchIcon,
    component: Lookup,
    permission: 'ADMIN_LOOKUP',
  },
  {
    path: '/feature-flags',
    label: 'Feature Flags',
    icon: FlagIcon,
    component: FeatureFlags,
    permission: 'ADMIN_FEATURE_FLAGS',
  },
  {
    path: '/queues',
    label: 'Queues',
    icon: QueueIcon,
    component: Queues,
    permission: 'ADMIN_QUEUES',
  },
  {
    path: '/jobs',
    label: 'Scheduled Jobs',
    icon: ScheduleIcon,
    component: Jobs,
    permission: 'ADMIN_QUEUES',
  },
  {
    path: '/map',
    label: 'Map',
    icon: MapIcon,
    component: Map,
    permission: 'ADMIN_MAP',
  },
  {
    path: '/grower-export',
    label: 'Grower Export',
    icon: PersonIcon,
    component: GrowerExport,
    permission: 'ADMIN_GROWER_EXPORT',
  },
  {
    path: '/farmqa',
    label: 'FarmQA',
    icon: ScienceIcon,
    component: FarmQA,
    permission: 'ADMIN_FARMQA',
  },
  {
    path: '/downloads',
    label: 'Downloads',
    icon: CloudDownloadIcon,
    component: Downloads,
    permission: 'ADMIN_DOWNLOADS',
  },
  {
    label: 'Uploads',
    icon: CloudUploadIcon,
    children: [
      {
        path: '/uploads/lookup',
        label: 'Lookup',
        component: UploadsLookup,
        permission: 'ADMIN_UPLOADS',
      },
      {
        path: '/uploads/planting',
        label: 'Planting',
        component: PlantingUploads,
        permission: 'ADMIN_UPLOADS',
      },
      {
        path: '/uploads/yield',
        label: 'Yield',
        component: YieldUploads,
        permission: 'ADMIN_UPLOADS',
      },
    ],
  },
  {
    label: 'Deere',
    icon: AgricultureIcon,
    children: [
      {
        path: '/deere/tokens',
        label: 'Access Tokens',
        component: DeereTokens,
        permission: 'ADMIN_DEERE',
      },
      {
        path: '/deere/subscriptions',
        label: 'Subscriptions',
        component: DeereSubscriptions,
        permission: 'ADMIN_DEERE',
      },
    ],
  },
];

const filterRoute = (route, userPermissions) => {
  if (route.children) {
    const children = route.children
      .map((child) => filterRoute(child, userPermissions))
      .filter(Boolean);
    return children.length > 0 ? { ...route, children } : null;
  }
  return userPermissions.includes(route.permission) ? route : null;
};

const filterRoutes = (routes, userPermissions) =>
  routes.map((route) => filterRoute(route, userPermissions)).filter(Boolean);

export function useRoutes() {
  const userPermissions = usePermissions();

  return useMemo(
    () => filterRoutes(allRoutes, userPermissions),
    [userPermissions],
  );
}
